import React, { useEffect } from 'react';
import { Alert, Button, IconAttention, styled, useDropdownOptions } from '@payright/web-components';
import Spinner from '@merchant-portal/components/spinner';
import { FormProvider, useForm } from 'react-hook-form';
import {
  ControlledDropdown,
  ControlledDropdownWithInput,
  ControlledInputField,
} from '@merchant-portal/components/form-fields';
import { WhatsThisTooltip } from '@merchant-portal/components/whats-this-tooltip';
import getConstants from '@merchant-portal/util/constants';
import { FormVerticalSection } from '@merchant-portal/components/form-vertical-section';
import { CustomerHouseholdDetailsData } from '@merchant-portal/types/customer';

const {
  DEPENDANTS_TOOLTIP,
  DEPENDANTS_DROPDOWN,
  FINANCIAL_EXPENSES_TOOLTIP,
  LIVING_EXPENSES_TOOLTIP,
  LOAN_COUNT_DESCRIPTION,
  LOAN_COUNT_TOOLTIP,
  HOUSEHOLD_INCOME_TOOLTIP,
  INCOME_FREQUENCIES,
  RELATIONSHIP_STATUS,
  RELATIONSHIP_STATUS_TOOLTIP,
  INCOME_PERCENTAGES,
} = getConstants();

export type FormHouseholdExpensesFormData = {
  householdDetails: {
    dependants: string;
    relationshipStatus: string;
    incomePercentage: string;
    appliedPercentage: string;
  };
  expenseDetails: {
    financialExpensesAmount: string;
    financialExpensesFrequency: string;
    livingExpensesAmount: string;
    livingExpensesFrequency: string;
    loanCount: string;
  };
};

type FormHouseholdExpensesProps = {
  showLivingExpenses: boolean;
  showFinancialExpenses: boolean;
  householdDetails: CustomerHouseholdDetailsData;
  errorMessage: string | null | undefined;
  loading: boolean;
  onSubmit: (formData: FormHouseholdExpensesFormData) => void;
};

const SCTooltipContent = styled.div`
  .underline {
    text-decoration: underline;
  }

  .bold {
    font-weight: 700;
  }
`;

const SCForm = styled.form`
  .label-with-tooltip {
    display: flex;
    flex-wrap: wrap;
    column-gap: 0.5rem;
    row-gap: 0.5rem;
    align-items: baseline;

    .label-secondary {
      width: 100%;
      color: ${(props) => props.theme.colours.plum.light};
      font-weight: lighter;
    }
  }

  .underline {
    text-decoration: underline;
  }
`;

export const FormHouseholdExpenses = ({
  showFinancialExpenses,
  showLivingExpenses,
  householdDetails,
  errorMessage,
  loading,
  onSubmit,
}: FormHouseholdExpensesProps) => {
  const reactHookForm = useForm<FormHouseholdExpensesFormData>({
    defaultValues: {
      expenseDetails: {
        financialExpensesAmount: '',
        financialExpensesFrequency: 'Monthly',
        livingExpensesAmount: '',
        livingExpensesFrequency: 'Monthly',
        loanCount: '',
      },
      householdDetails: {
        dependants: householdDetails.dependants || (null as unknown as string), // we do not let you submit null but it is the starting value
        appliedPercentage: '%',
        incomePercentage: '',
        relationshipStatus: householdDetails.relationshipStatus || (null as unknown as string),
      },
    },
  });

  const { handleSubmit, errors, watch, setValue, unregister } = reactHookForm;
  const relationshipStatus = watch('householdDetails.relationshipStatus');

  const [incomePercentages, temporarilyAddAnotherOption] = useDropdownOptions(
    INCOME_PERCENTAGES,
    '%'
  );

  const handleIncomePercentageChange = (value: string) => {
    const percentage = typeof value === 'string' ? parseFloat(value) : value;
    temporarilyAddAnotherOption(percentage);
    setValue('householdDetails.appliedPercentage', `${value}%`);
    setValue('householdDetails.incomePercentage', `${value}`);
  };

  useEffect(() => {
    if (relationshipStatus === 'Single') {
      unregister('householdDetails.incomePercentage');
      unregister('householdDetails.appliedPercentage');
    }
  }, [relationshipStatus, unregister]);

  return (
    <FormProvider {...reactHookForm}>
      <SCForm onSubmit={handleSubmit(onSubmit)} autoComplete="off" className="expense-body">
        {showLivingExpenses && (
          <FormVerticalSection title="Expenses">
            <ControlledDropdownWithInput
              dropdownName="expenseDetails.livingExpensesFrequency"
              inputName="expenseDetails.livingExpensesAmount"
              inputPrefix="$"
              options={INCOME_FREQUENCIES}
              inputRules={{
                required: 'Living expenses is required',
                min: {
                  value: 0,
                  message: 'Living expenses cannot be negative',
                },
                validate: (value: any) => {
                  if (isNaN(value)) {
                    return 'Living expenses must be a number';
                  }
                },
              }}
              error={errors?.expenseDetails?.livingExpensesAmount?.message}
            >
              <div className="label-with-tooltip">
                <span>
                  <span className="underline">Your share of</span> living expenses (excluding rent
                  and debt repayments)
                </span>
                <WhatsThisTooltip content={LIVING_EXPENSES_TOOLTIP} />{' '}
                <span className="label-secondary">
                  If you share any expenses, only include{' '}
                  <span className="underline">your own portion</span>
                </span>
              </div>
            </ControlledDropdownWithInput>
            <ControlledInputField
              error={errors?.expenseDetails?.loanCount?.message}
              name="expenseDetails.loanCount"
              rules={{
                required: 'Please enter a number between 0 & 100',
                min: {
                  value: 0,
                  message: 'Please enter a number between 0 & 100',
                },
                max: {
                  value: 100,
                  message: 'Please enter a number between 0 & 100',
                },
              }}
              decimals={0}
              type="number"
            >
              <div className="label-with-tooltip">
                {LOAN_COUNT_DESCRIPTION}
                <WhatsThisTooltip content={LOAN_COUNT_TOOLTIP} />
              </div>
            </ControlledInputField>
            {showFinancialExpenses && (
              <ControlledDropdownWithInput
                dropdownName="expenseDetails.financialExpensesFrequency"
                inputName="expenseDetails.financialExpensesAmount"
                inputPrefix="$"
                options={INCOME_FREQUENCIES}
                inputRules={{
                  required: 'Financial expenses is required',
                  min: {
                    value: 0,
                    message: 'Financial expenses cannot be negative',
                  },
                  validate: (value: any) => {
                    if (isNaN(value)) {
                      return 'Financial expenses  must be a number';
                    }
                  },
                }}
                error={errors?.expenseDetails?.financialExpensesAmount?.message}
              >
                <div className="label-with-tooltip">
                  <span>
                    <span className="underline">Your share</span> of financial expenses (excluding
                    any mortgage repayments)
                  </span>
                  <WhatsThisTooltip content={FINANCIAL_EXPENSES_TOOLTIP} />{' '}
                  <span className="label-secondary">
                    If you share any expenses, only include{' '}
                    <span className="underline">your own portion</span>
                  </span>
                </div>
              </ControlledDropdownWithInput>
            )}
            <hr />
          </FormVerticalSection>
        )}
        <FormVerticalSection title="Household Situation">
          <ControlledDropdown
            placeholder="Please select"
            name="householdDetails.relationshipStatus"
            rules={{ required: 'Relationship Status is required' }}
            error={errors?.householdDetails?.relationshipStatus?.message}
            className="left"
            options={RELATIONSHIP_STATUS}
          >
            <div className="label-with-tooltip">
              Relationship Status
              <WhatsThisTooltip content={RELATIONSHIP_STATUS_TOOLTIP} />
            </div>
          </ControlledDropdown>
          <ControlledDropdown
            placeholder="Please select"
            rules={{
              required: 'Dependants is required',
            }}
            error={errors?.householdDetails?.dependants?.message}
            name="householdDetails.dependants"
            className="right"
            options={DEPENDANTS_DROPDOWN}
          >
            <div className="label-with-tooltip">
              Dependants
              <WhatsThisTooltip content={DEPENDANTS_TOOLTIP} />
            </div>
          </ControlledDropdown>
          {relationshipStatus !== 'Single' && (
            <ControlledDropdownWithInput
              dropdownName="householdDetails.appliedPercentage"
              inputName="householdDetails.incomePercentage"
              options={incomePercentages}
              inputSuffix="%"
              inputRules={{
                required: 'Your share of household income is required',
                min: {
                  value: 0,
                  message: 'Please enter a number between 0 & 100',
                },
                max: {
                  value: 100,
                  message: 'Please enter a number between 0 & 100',
                },
                validate: (value: any) => {
                  if (isNaN(value)) {
                    return 'Your share of household income must be a number';
                  }
                },
              }}
              error={errors?.householdDetails?.incomePercentage?.message}
              onInputChange={handleIncomePercentageChange}
              onDropdownChange={handleIncomePercentageChange}
            >
              <div className="label-with-tooltip">
                <span>
                  <span className="underline">Your share</span> of Household Income *
                </span>
                <WhatsThisTooltip content={HOUSEHOLD_INCOME_TOOLTIP} />{' '}
                <span className="label-secondary">
                  What portion of your household’s income comes from you?
                </span>
              </div>
            </ControlledDropdownWithInput>
          )}
          <hr />
        </FormVerticalSection>

        <FormVerticalSection>
          {errorMessage && (
            <Alert
              title={'Application error'}
              body={errorMessage}
              outcome="error"
              icon={<IconAttention />}
            />
          )}
          <div className="expense-buttons">
            <Button
              maxWidth="100%"
              type="submit"
              iconPosition="right"
              icon={loading ? <Spinner /> : undefined}
              disabled={loading}
              colour="primary"
              className="next"
            >
              Next
            </Button>
            <Button maxWidth="100%" handleClick={() => {}} type="button" colour="blue" outline>
              Cancel
            </Button>
          </div>
        </FormVerticalSection>
      </SCForm>
    </FormProvider>
  );
};
